// Conversation-debug data for the operator console. A diagnostic
// view of one conversation: the thread on the left, and — for a selected bot
// turn — the full pipeline breakdown (classification, RAG retrieval, generation
// cost/latency, handoff evaluation) on the right. Admin-only.
//
// Wired: getConversationDebug() -> GET
// /admin/conversations/:id/debug, the observability trace built from messages +
// state transitions + llm_calls. Classification (intent/confidence), generation
// cost/latency/tokens, and handoff are real; RAG retrieval is not logged per turn
// in Phase 1, so it surfaces as "0 de 0" rather than fabricated chunks. Returns
// null (404) for an unknown id.

import { apiClient } from '../../api/client.js'

export interface DebugPill {
  tone: 'ok' | 'sens'
  label: string
}
export interface DebugStat {
  label: string
  value: string
  sub: string
  tone?: 'warn' | 'mono'
}

export type ThreadItem =
  | { type: 'day'; id: string; label: string }
  | {
      type: 'msg'
      id: string
      sender: 'user' | 'bot' | 'agent'
      author: string
      time: string
      text: string
      /** Bot turns carry a pipeline trace; clicking inspects it. */
      inspectionId?: string
    }
  | { type: 'event'; id: string; text: string }
  | { type: 'note'; id: string; text: string }

export interface ClassRow {
  k: string
  label: string
  score?: number
  tone?: 'ok'
}
export interface RagChunk {
  source: string
  page: string
  score: number
  snippet: string
}
export interface GenRow {
  k: string
  v: string
  tone?: 'ok'
}
export interface Inspection {
  id: string
  turnTitle: string
  msgId: string
  time: string
  userMessage: string
  classification: ClassRow[]
  rag: { used: number; total: number; chunks: RagChunk[] }
  generation: GenRow[]
  handoff: { rulesEvaluated: number; triggered: string; result: string }
}

export interface ConversationDebug {
  contact: { name: string; initials: string }
  pills: DebugPill[]
  meta: string[]
  stats: DebugStat[]
  thread: ThreadItem[]
  inspections: Record<string, Inspection>
}

/**
 * GET /admin/conversations/:id/debug — the pipeline trace (classification,
 * RAG, generation cost/latency, handoff) per bot turn. Resolves null on any
 * fetch error so the page renders its not-found state instead of throwing.
 */
export async function getConversationDebug(id: string): Promise<ConversationDebug | null> {
  try {
    const res = await apiClient.get<{ data: ConversationDebug }>(
      `/admin/conversations/${encodeURIComponent(id)}/debug`,
    )
    return res.data.data
  } catch {
    // 404 (unknown conversation) -> the page renders its not-found state.
    return null
  }
}

/** Operator unstick: return a conversation parked in handoff to the bot (the
 *  concierge equivalent of the tenant inbox's return-to-bot). Throws on 409 when
 *  the conversation isn't in a handoff state. */
export async function returnConversationToBot(id: string): Promise<void> {
  await apiClient.post(`/admin/conversations/${encodeURIComponent(id)}/return-to-bot`, {})
}
